import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IFormControl } from "./FormControl";
import GUI from "../GUI";
import Timeout from "../../libs/observer/Timeout";

export type TFormContext = {
    update$:Timeout
    valid:boolean
    dirty:boolean
    controls:Record<string,IFormControl>
    setDirty:(dirty:boolean) => void
    register:(id:string, control:IFormControl) => void
    unregister:(id:string) => void
}
const FormContext = createContext<TFormContext>({} as TFormContext)

export const useForm = () => useContext(FormContext)

export type TFormProps = React.ComponentPropsWithoutRef<"div"> & {
    updateDelay?:number
    defaultValid?:boolean
    defaultDirty?:boolean
}

export default function Form(props:TFormProps) {
    const {children,className,defaultValid,defaultDirty,updateDelay,...formProps} = props
    const [valid,setValid] = useState<boolean>(defaultValid||false)
    const [dirty,setDirty] = useState<boolean>(defaultDirty||false)
    const [controls,setControls] = useState<Record<string,IFormControl>>({})
    const controlsRef = useRef<Record<string,IFormControl>>(controls)
    const update$ = useMemo(() => new Timeout(updateDelay ?? 250), [])

    useEffect(() => {
        controlsRef.current = controls
    }, [controls])
    
    const onUpdate = useCallback((id:string, control:IFormControl) => {
        setValid(valid => {
            var valid = true
            for(const control of Object.values(controlsRef.current)) {
                if (!control.valid) {
                    valid = false
                    break;
                }
            }
            update$.next()
            return valid
        })
    }, [])
    
    const register = (id:string, control:IFormControl) => {
        setControls( controls => {
            controls[id] = control
            control.on("update", onUpdate)
            return {...controls}
        })
    }
    const unregister = (id:string) => {
        setControls( controls => {
            const control = controls[id]
            control?.removeListener("update", onUpdate)
            delete controls[id]
            return {...controls}
        })
    }
    return <FormContext.Provider value={{register,unregister,controls,valid,dirty,update$,setDirty}}>
        <div className={GUI.joinClass("form", className, valid ? "valid" : "invalid", dirty ? "dirty" : "pristine")} {...formProps}>
            {children}
        </div>
    </FormContext.Provider>
}